import { useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/apiClient";
import { Modal } from "./common";
import { BarChart3, CalendarDays, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { AppointmentReport } from "./AppointmentReport";
import { PageTitle, Empty } from "./common";
import { WaTemplateButton } from "./WaTemplatePicker";
import { AppointmentDetail } from "./AppointmentDetail";
import { RESULT_COLORS, dayTitle, groupByDate, monthTitle, sortAppts, todayStr, ymd } from "../lib/appt";

const TABS = [["today", "Bugün"], ["upcoming", "Yaklaşan"], ["past", "Geçmiş"], ["calendar", "Takvim"]];
const WEEK = ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"];

function ApptRow({ a, onOpen }) {
  return (
    <li className="appt-row" data-testid={`appt-row-${a.id}`}>
      <button type="button" className="appt-row-main" onClick={() => onOpen(a)} data-testid={`appt-open-${a.id}`}>
        <span className="appt-time">{a.time}</span>
        <span className="appt-who">
          <strong>{a.customerName}{a.customerKind === "lead" && <em className="appt-lead-tag">Aday</em>}</strong>
          <span className="appt-phone">{a.phone || "Telefon yok"}</span>
          <small>{a.subject || "Randevu"} · {a.mode}</small>
        </span>
        {a.result && <span className={`badge badge-${RESULT_COLORS[a.result]}`}>{a.result}</span>}
      </button>
      <div className="appt-row-actions">
        {a.phone && <a className="mini-btn call" href={`tel:${a.phone}`} data-testid={`appt-call-${a.id}`}><Phone size={13} /> Ara</a>}
        <WaTemplateButton appt={a} />
      </div>
    </li>
  );
}

function Grouped({ list, onOpen, empty }) {
  if (!list.length) return <Empty text={empty} />;
  return groupByDate(list).map(([date, items]) => (
    <section key={date} className="appt-group" data-testid={`appt-group-${date}`}>
      <h3>{dayTitle(date)}<b>{items.length}</b></h3>
      <ul className="appt-list">{items.map((a) => <ApptRow key={a.id} a={a} onOpen={onOpen} />)}</ul>
    </section>
  ));
}

function MiniCalendar({ month, setMonth, selected, onSelect, marks }) {
  const [y, m] = month;
  const first = new Date(y, m, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => ymd(new Date(y, m, i + 1)))];
  const shift = (d) => { const n = new Date(y, m + d, 1); setMonth([n.getFullYear(), n.getMonth()]); };
  return (
    <div className="mini-cal" data-testid="appt-calendar">
      <div className="mini-cal-head">
        <button type="button" className="icon-button" onClick={() => shift(-1)} data-testid="appt-cal-prev"><ChevronLeft size={16} /></button>
        <strong data-testid="appt-cal-title">{monthTitle(y, m)}</strong>
        <button type="button" className="icon-button" onClick={() => shift(1)} data-testid="appt-cal-next"><ChevronRight size={16} /></button>
      </div>
      <div className="mini-cal-grid">
        {WEEK.map((w) => <span key={w} className="mini-cal-wd">{w}</span>)}
        {cells.map((d, i) => d ? (
          <button key={d} type="button" className={`mini-cal-day${d === selected ? " sel" : ""}${d === todayStr() ? " today" : ""}`} onClick={() => onSelect(d)} data-testid={`appt-cal-day-${d}`}>
            {Number(d.slice(8))}{marks[d] && <i>{marks[d]}</i>}
          </button>
        ) : <span key={`e${i}`} />)}
      </div>
    </div>
  );
}

const DAY_NAMES = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
const mondayOf = (s) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };

function WeekView({ start, setStart, list, onOpen }) {
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  const shift = (n) => { const d = new Date(start); d.setDate(d.getDate() + n * 7); setStart(d); };
  const last = days[6];
  const title = `${days[0].getDate()} ${monthTitle(days[0].getFullYear(), days[0].getMonth()).split(" ")[0]} – ${last.getDate()} ${monthTitle(last.getFullYear(), last.getMonth())}`;
  return (
    <div className="week-view" data-testid="appt-week-view">
      <div className="mini-cal-head">
        <button type="button" className="icon-button" onClick={() => shift(-1)} data-testid="appt-week-prev"><ChevronLeft size={16} /></button>
        <strong data-testid="appt-week-title">{title}</strong>
        <button type="button" className="icon-button" onClick={() => shift(1)} data-testid="appt-week-next"><ChevronRight size={16} /></button>
      </div>
      <small className="week-view-hint">Tarihini veya saatini değiştirmek için randevuya dokunun.</small>
      <div className="week-grid">
        {days.map((d, i) => {
          const key = ymd(d);
          const items = list.filter((a) => a.date === key);
          return (
            <div key={key} className={`week-day${key === todayStr() ? " today" : ""}`} data-testid={`appt-week-day-${key}`}>
              <div className="week-day-head"><b>{d.getDate()}</b><span>{DAY_NAMES[i]}</span>{items.length > 0 && <em>{items.length}</em>}</div>
              <div className="week-day-items">
                {items.length === 0 ? <small className="week-empty">—</small> : items.map((a) => (
                  <button key={a.id} type="button" className={`week-item${a.result ? " done" : ""}`} onClick={() => onOpen(a)} data-testid={`appt-week-item-${a.id}`}>
                    <b>{a.time}</b><span>{a.customerName}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MoveSheet({ appt, onMoved, onOpenDetail, onClose }) {
  const [date, setDate] = useState(appt.date);
  const [time, setTime] = useState(appt.time);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await axios.put(`${API}/appointments/${appt.id}`, { ...appt, date, time }); onMoved(data); }
    catch (e) { window.alert(e?.response?.data?.detail || "Taşınamadı"); setBusy(false); }
  };
  return (
    <Modal title="Randevuyu taşı" onClose={onClose}>
      <div className="move-sheet" data-testid="appt-move-sheet">
        <p><strong>{appt.customerName}</strong> · {dayTitle(appt.date)} {appt.time}{appt.subject ? ` · ${appt.subject}` : ""}</p>
        <div className="form-grid">
          <label className="field"><span>Yeni tarih</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-move-date" /></label>
          <label className="field"><span>Yeni saat</span><input type="time" step="300" value={time} onChange={(e) => setTime(e.target.value)} data-testid="appt-move-time" /></label>
        </div>
        <div className="modal-actions">
          <button type="button" className="outline-button" onClick={onOpenDetail} data-testid="appt-move-open-detail">Detayı aç</button>
          <button type="button" className="primary-button" disabled={busy || !date || !time || (date === appt.date && time === appt.time)} onClick={save} data-testid="appt-move-save">Taşı</button>
        </div>
      </div>
    </Modal>
  );
}

export function Appointments({ appointments, customers, leads, onNew, onEdit, onUpdated, onDeleted }) {
  const [tab, setTab] = useState("today");
  const [openId, setOpenId] = useState(null);
  const [report, setReport] = useState(false);
  const [calView, setCalView] = useState("month");
  const [movingId, setMovingId] = useState(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayStr()));
  const [selected, setSelected] = useState(todayStr());
  const [month, setMonth] = useState(() => [new Date().getFullYear(), new Date().getMonth()]);
  const today = todayStr();
  const sorted = useMemo(() => sortAppts(appointments), [appointments]);
  const lists = {
    today: sorted.filter((a) => a.date === today),
    upcoming: sorted.filter((a) => a.date > today),
    past: sorted.filter((a) => a.date < today).reverse(),
    calendar: sorted.filter((a) => a.date >= selected),
  };
  const marks = useMemo(() => sorted.reduce((m, a) => ({ ...m, [a.date]: (m[a.date] || 0) + 1 }), {}), [sorted]);
  const open = sorted.find((a) => a.id === openId);
  const personOf = (a) => (a.customerKind === "lead" ? { ...leads.find((l) => l.id === a.customerId), kind: "lead" } : customers.find((c) => c.id === a.customerId));
  const empty = { today: "Bugün randevunuz yok", upcoming: "Yaklaşan randevu yok", past: "Geçmiş randevu yok", calendar: "Seçilen tarihten sonra randevu yok" }[tab];
  return (
    <div data-testid="appointments-page">
      <PageTitle eyebrow="AJANDA" title="Randevular" description="Müşteri ve aday görüşmelerinizi planlayın, hatırlatma alın." action="Yeni Randevu" onAction={onNew} actionTest="appt-new-button" />
      <button type="button" className="outline-button appt-report-btn" onClick={() => setReport(true)} data-testid="appt-report-button"><BarChart3 size={14} /> Aylık randevu raporu</button>
      <div className="appt-tabs" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" className={tab === k ? "active" : ""} onClick={() => setTab(k)} data-testid={`appt-tab-${k}`}>
            {k === "calendar" && <CalendarDays size={14} />}{l}{k !== "calendar" && <b>{lists[k].length}</b>}
          </button>
        ))}
      </div>
      <section className="panel appt-panel">
        {tab === "calendar" && (
          <div className="segmented cal-view-toggle" data-testid="appt-cal-view">
            <button type="button" className={calView === "month" ? "active" : ""} onClick={() => setCalView("month")} data-testid="appt-cal-view-month">Ay</button>
            <button type="button" className={calView === "week" ? "active" : ""} onClick={() => { setWeekStart(mondayOf(selected)); setCalView("week"); }} data-testid="appt-cal-view-week">Hafta</button>
          </div>
        )}
        {tab === "calendar" && calView === "week" ? <WeekView start={weekStart} setStart={setWeekStart} list={sorted} onOpen={(a) => setMovingId(a.id)} /> : (
          <>
            {tab === "calendar" && <MiniCalendar month={month} setMonth={setMonth} selected={selected} onSelect={setSelected} marks={marks} />}
            <Grouped list={lists[tab]} onOpen={(a) => setOpenId(a.id)} empty={empty} />
          </>
        )}
      </section>
      {movingId && sorted.find((a) => a.id === movingId) && <MoveSheet appt={sorted.find((a) => a.id === movingId)} onClose={() => setMovingId(null)}
        onOpenDetail={() => { setOpenId(movingId); setMovingId(null); }} onMoved={(a) => { onUpdated(a); setMovingId(null); }} />}
      {report && <AppointmentReport appointments={appointments} leads={leads} customers={customers} onClose={() => setReport(false)} />}
      {open && <AppointmentDetail appt={open} all={appointments} person={personOf(open)} onClose={() => setOpenId(null)}
        onEdit={() => { setOpenId(null); onEdit(open); }} onUpdated={onUpdated} onDeleted={(id, tid) => { setOpenId(null); onDeleted(id, tid); }} />}
    </div>
  );
}
